import type { FsId } from '@fullstory/recording-api';
import { NormalizedTagName, OriginalTagAttr } from '@fullstory/rewriters';
import type { FsDocument } from './fsdocument';
import { FsNode } from './fsnode';
import { SvgNamespace } from './svg';

/** Shared behavior for image-like vDOM nodes. */
export abstract class FsImagelikeElement extends FsNode {
  protected createNode(doc: Document): Element {
    const dom = doc.createElement('img');
    this.addRenderingMayChangeListener(dom);
    return dom;
  }

  protected addLoadingResource(dom: Element, href: string): void {
    // Use a subsecond FOUC timeout for images, since they're less critical
    // than CSS (related to CH66085).
    const resolvedLoaded = this.ownerDoc.addLoadingResource(href, 'image', 750);
    dom.addEventListener('load', () => resolvedLoaded('load'));
    dom.addEventListener('error', () => resolvedLoaded('error'));
  }

  protected addRenderingMayChangeListener(dom: Element): void {
    dom.addEventListener('load', this.ownerDoc.handlers.onRenderingMayChange);
    dom.addEventListener('error', this.ownerDoc.handlers.onRenderingMayChange);
  }
}

/** The vDOM representation of an {@link HTMLImageElement} node. */
export class FsImageElement extends FsImagelikeElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'img', undefined, fsid);
  }

  /** @override */
  protected flushAttr(dom: HTMLImageElement, name: string, value: string | null): void {
    if (name === 'src' && value !== null) {
      super.addLoadingResource(dom, value);
    }

    super.flushAttr(dom, name, value);
  }
}

/** The vDOM representation of an {@link SVGImageElement} node. */
export class FsSvgImageElement extends FsImagelikeElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'image' as NormalizedTagName, SvgNamespace, fsid);
  }

  protected createNode(doc: Document): SVGImageElement {
    const dom = doc.createElementNS(SvgNamespace, 'image');
    this.addRenderingMayChangeListener(dom);
    return dom;
  }

  /** @override */
  protected flushAttr(dom: SVGImageElement, name: string, value: string | null): void {
    if ((name === 'href' || name === 'xlink:href') && value !== null) {
      super.addLoadingResource(dom, value);
    }

    super.flushAttr(dom, name, value);
  }
}

/**
 * Shared behavior for <embed>-like elements that can contain images.
 *
 * We render these as {@link HTMLImageElement}s during playback to ensure that
 * they only load image resources. This is because they're a potential source of
 * XSS:
 *  - <object> can render HTML error pages even if its type attribute isn't
 *    'text/html'.
 *  - SVG loaded in an <embed> or <object> can execute JS.
 */
export abstract class FsFakeImagelikeElement extends FsImagelikeElement {
  constructor(ownerDoc: FsDocument, tag: NormalizedTagName, fsid?: FsId) {
    super(ownerDoc, tag, undefined, fsid);

    // Make the original tag name for this element available for use in
    // rewritten CSS. See createNode() for more details.
    this.setAttr(OriginalTagAttr, tag, /* skipRewrite */ true);
  }
}

/** The vDOM representation of an {@link HTMLEmbedElement} node. */
export class FsEmbedElement extends FsFakeImagelikeElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'embed', fsid);
  }

  /** @override */
  protected flushAttr(dom: HTMLImageElement, name: string, value: string | null): void {
    if (name === 'src' && value !== null) {
      super.addLoadingResource(dom, value);
    }

    super.flushAttr(dom, name, value);
  }
}

/** The vDOM representation of an {@link HTMLObjectElement} node. */
export class FsObjectElement extends FsFakeImagelikeElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'object', fsid);
  }

  /** @override */
  protected flushAttr(dom: HTMLImageElement, name: string, value: string | null): void {
    if (name === 'data') {
      if (value !== null) {
        super.addLoadingResource(dom, value);
      }

      // Remap <object data> to <img src>.
      super.flushAttr(dom, 'src', value);
      return;
    }

    super.flushAttr(dom, name, value);
  }
}
